// Considered: a bespoke "N of M" progress atom — rejected because the count is a plain sentence
// plus one TagAtom marker, not a reusable numeric-progress shape; nothing else in the design
// system reads a roster-coverage tuple this way. See spec Reuse Audit.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import type { RosterCoverage } from 'types/spherePoints';
import TagAtom from '../../../atoms/Tag/TagAtom';
import { buildReadsAsSentence, type ReadsAsExclusions } from '../readsAsSentence';
import { RollupSetModel } from '../rollupSetModel';
import type { PropertyOption } from '../../PropertyFilter/PropertyFilterMolecule';
import type { RelationshipOption } from '../../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../../PerspectiveFilter/PerspectiveFilterMolecule';
import './ReadsAsBand.scss';

export interface ReadsAsBandProps {
  value: RollupSetModel;
  /**
   * Last-applied roster coverage from the server (`getStatusFactors`'s `coverage` field) —
   * undefined before the factor has ever resolved. Never client-recalculated: this band only
   * ever reads what the server last computed.
   */
  coverage?: RosterCoverage;
  /** True while local edits haven't been applied/saved yet — the count stays the last-applied value. */
  isDirty?: boolean;
  /** The roster's exclusions; absent when no roster is mounted. */
  excluded?: ReadsAsExclusions;
  availableProperties?: PropertyOption[];
  availableRelationships?: RelationshipOption[];
  availablePerspectives?: PerspectiveOption[];
  className?: string;
}

const ReadsAsBand: React.FC<ReadsAsBandProps> = ({
  value,
  coverage,
  isDirty = false,
  excluded,
  availableProperties,
  availableRelationships,
  availablePerspectives,
  className = '',
}) => {
  const { t, i18n } = useTranslation();

  // The last coverage the server actually returned, kept across a transient `undefined` so the
  // count never blanks (spec Band 04: "keep last known value and mark stale. Never blank.").
  const [lastKnownCoverage, setLastKnownCoverage] = useState<RosterCoverage | undefined>(coverage);
  useEffect(() => {
    if (coverage) setLastKnownCoverage(coverage);
  }, [coverage]);

  const displayedCoverage = coverage ?? lastKnownCoverage;
  const isStale = !coverage && !!lastKnownCoverage;

  const sentence = buildReadsAsSentence({
    set: value,
    t,
    excluded,
    availableProperties,
    availableRelationships,
    availablePerspectives,
    locale: i18n?.language,
  });

  const unmeasured = displayedCoverage ? displayedCoverage.total - displayedCoverage.reporting : 0;

  return (
    <div className={`reads-as-band ${className}`} data-testid="reads-as-band">
      <div className="reads-as-band__sentence-row">
        <span className="reads-as-band__label">{t('statusFactors.set.readsAs.label', 'Reads as')}</span>
        <span className="reads-as-band__sentence">{sentence}</span>
      </div>

      <div className="reads-as-band__count-row">
        {displayedCoverage ? (
          <span className="reads-as-band__count" data-testid="reads-as-count">
            {t('statusFactors.set.readsAs.count', '→ {{count}} spheres in this set', {
              count: displayedCoverage.total,
            })}
          </span>
        ) : (
          <span className="reads-as-band__count reads-as-band__count--pending" data-testid="reads-as-count-pending">
            {t('statusFactors.set.readsAs.countPending', 'count available after the first save')}
          </span>
        )}

        {isDirty && (
          <TagAtom
            tone="warn"
            label={t('statusFactors.set.readsAs.dirtyMarker', 'unsaved edits — count not applied yet')}
            data-testid="reads-as-dirty-marker"
          />
        )}
        {!isDirty && isStale && (
          <TagAtom
            tone="warn"
            label={t('statusFactors.set.readsAs.staleMarker', 'count unavailable — showing last known')}
            data-testid="reads-as-stale-marker"
          />
        )}
      </div>

      {displayedCoverage && (
        <p className="reads-as-band__breakdown" data-testid="reads-as-breakdown">
          {t('statusFactors.set.readsAs.breakdown', '{{reporting}} reporting, {{unmeasured}} enrolled', {
            reporting: displayedCoverage.reporting,
            unmeasured,
          })}
        </p>
      )}
    </div>
  );
};

ReadsAsBand.displayName = 'ReadsAsBand';

export default ReadsAsBand;
